import React, { useState, useEffect, useCallback } from 'react';
import { collection, getCountFromServer, getDocs, doc, getDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { authService } from '../../services/authService';

const API_BASE = process.env.REACT_APP_NETLIFY_FUNCTIONS_URL || '/.netlify/functions';

const RECON_COLECCIONES = [
  { coleccion: 'ordenes',     label: 'Órdenes'     },
  { coleccion: 'incidencias', label: 'Incidencias' },
  { coleccion: 'productos',   label: 'Productos'   },
  { coleccion: 'servicios',   label: 'Servicios'   },
];

// window.prompt() no funciona dentro del navegador embebido de VS Code (ni
// en otros webviews) — "prompt() is not supported" (Rafa probándolo ahí
// mismo, 2026-10-15). Las acciones destructivas de esta pantalla pedían
// escribir "BORRAR" con prompt() como segunda confirmación; se sustituye por
// un segundo confirm() (que sí funciona en ese entorno), igual de explícito.
const confirmarBorradoDefinitivo = () =>
  window.confirm('Última confirmación: esto borra datos de verdad y no se puede deshacer. ¿Seguro?');

const RECON_VACIO = () => ({ running: false, creados: 0, actualizados: 0, coinciden: 0, omitidosPendientes: 0, errores: 0, done: false, error: null });
const HUERFANOS_VACIO = () => ({ loading: false, count: null, ids: [], totalFirebase: null, totalStelOrder: null, error: null, borrando: false, borrado: null });

// 'Facturas'/'Facturas abono' no están en esta lista genérica a propósito —
// ese import es en bruto (sin enrichment) y duplicaba en `facturas` lo que
// ya sube curado syncFacturas.js. Tienen su propia tarjeta más abajo
// ("Facturas — histórico completo"), que llama a forzarSyncFacturas con
// full:true en vez de al import genérico de esta pantalla.
const TABLAS = [
  { tabla: 'workOrders',       coleccion: 'ordenes',       label: 'Órdenes de trabajo' },
  { tabla: 'clients',          coleccion: 'clientes',      label: 'Clientes'            },
  { tabla: 'assets',           coleccion: 'activos',       label: 'Activos / Equipos'   },
  { tabla: 'incidents',        coleccion: 'incidencias',   label: 'Incidencias'         },
  { tabla: 'assetImages',      coleccion: 'activoImagenes', label: 'Fotos de equipos'   },
  { tabla: 'purchaseOrders',   coleccion: 'pedidosCompra', label: 'Pedidos de compra'   },
  { tabla: 'workEstimates',    coleccion: 'presupuestos',  label: 'Presupuestos'        },
  { tabla: 'products',         coleccion: 'productos',     label: 'Productos'           },
  { tabla: 'services',         coleccion: 'servicios',     label: 'Servicios'           },
];

// ── API helpers ───────────────────────────────────────────────────────────────

async function callFn(fn, body) {
  const r = await fetch(`${API_BASE}/${fn}`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
    body:    JSON.stringify({ ...body }),
  });
  const texto = await r.text();
  if (!texto) {
    // Cuerpo vacío — casi siempre un timeout de la función en Netlify (se
    // corta a media ejecución, sin ocasión de devolver un JSON de error) en
    // vez de un fallo controlado. r.json() directamente sobre esto daba
    // "Unexpected end of JSON input", un mensaje que no dice nada útil.
    throw new Error(`${fn} no respondió (${r.status}) — puede que se haya cortado por timeout. Puede haberse aplicado parcialmente; vuelve a intentarlo.`);
  }
  try {
    return JSON.parse(texto);
  } catch {
    throw new Error(`${fn} devolvió una respuesta no válida (${r.status}): ${texto.slice(0, 200)}`);
  }
}

// ── Firebase: contar registros por colección ──────────────────────────────────

async function contarColecciones() {
  const counts = {};
  await Promise.allSettled(
    TABLAS.map(async ({ coleccion }) => {
      try {
        const snap = await getCountFromServer(collection(db, coleccion));
        counts[coleccion] = snap.data().count;
      } catch { counts[coleccion] = null; }
    })
  );
  return counts;
}

// ── Firebase: eliminar todos los docs de una colección ────────────────────────

async function limpiarColeccion(coleccion, onProgress) {
  const snap = await getDocs(collection(db, coleccion));
  const total = snap.docs.length;
  let deleted = 0;
  for (let i = 0; i < snap.docs.length; i += 400) {
    const batch = writeBatch(db);
    snap.docs.slice(i, i + 400).forEach(d => batch.delete(d.ref));
    await batch.commit();
    deleted += Math.min(400, snap.docs.length - i);
    onProgress?.(deleted, total);
  }
  return deleted;
}

// Productos/Servicios son colecciones-catálogo pequeñas (cientos, no miles
// de órdenes reales) — cuando StelOrder se purga de golpe (22.700→553,
// 2026-10-15) es más barato vaciar y reimportar de cero que comparar
// huérfanos uno a uno. El borrado en sí NO puede hacerse desde aquí (cliente)
// — firestore.rules bloquea `delete` en productos/servicios a propósito
// ("Missing or insufficient permissions", encontrado al probarlo en real) —
// así que vaciarYReimportar() más abajo llama al modo 'vaciar' de
// huerfanosStelOrder.js (Netlify Function, con la cuenta de servicio) en vez
// de borrar aquí mismo.
const CATALOGO_STELORDER = new Set(['productos', 'servicios']);

// ── Componente ────────────────────────────────────────────────────────────────

export default function MigracionView() {
  const [counts, setCounts]           = useState({});
  const [lastSync, setLastSync]       = useState(null);
  const [loadingCounts, setLoadingCounts] = useState(true);

  // Import inicial
  const [importando, setImportando]   = useState(false);
  const [importState, setImportState] = useState({}); // { [tabla]: { total, done, error } }

  // Import de una sola colección (independiente del import completo de arriba)
  const [importandoUna, setImportandoUna] = useState(null); // tabla en curso, o null
  const [importUna, setImportUna]         = useState({});   // { [tabla]: { total, done, error } }
  const [vaciando, setVaciando]           = useState(null); // tabla en curso, o null

  // Limpieza
  const [limpiando, setLimpiando]     = useState(false);
  const [limpiezaLog, setLimpiezaLog] = useState([]);

  // Catálogo offline de productos (técnicos, ver catalogoLocal.js) — normalmente
  // lo regenera solo generarCatalogoProductos.js de madrugada, pero tras
  // limpiar/reimportar Productos conviene poder forzarlo ya mismo en vez de
  // esperar al cron (encontrado 2026-10-15: el buscador de material en
  // Finalizar Visita seguía "sin resultados" porque el catálogo offline
  // todavía reflejaba los ~22.700 productos viejos).
  const [regenerandoCatalogo, setRegenerandoCatalogo] = useState(false);
  const [catalogoResult, setCatalogoResult]           = useState(null);

  // Sync manual
  const [syncing, setSyncing]         = useState(false);
  const [syncResult, setSyncResult]   = useState(null);

  const [facturasImporting, setFacturasImporting] = useState(false);
  const [facturasResult, setFacturasResult]       = useState(null);

  // Import Ariston (estados específicos)
  const [importandoAriston, setImportandoAriston] = useState(false);
  const [aristonResult, setAristonResult]         = useState(null);

  // Reconciliar preliquidación Ariston (limpiar drift de `ordenes`)
  const [reconPreliq, setReconPreliq]     = useState({ running: false, revisadas: 0, corregidas: 0, quedan: null, total: null, error: null, done: false });

  // Reconciliar órdenes/incidencias con StelOrder (compara y sincroniza lo distinto, sin borrar y reimportar)
  // Antes esto estaba escrito a mano solo para ordenes/incidencias — al
  // añadir Productos/Servicios a RECON_COLECCIONES se quedaron sin entrada
  // inicial (reconStel['productos'] era undefined) y la UI petaba con
  // "Cannot read properties of undefined (reading 'running')" en cuanto
  // RECON_COLECCIONES.map() intentaba pintar su tarjeta (Rafa, 2026-10-15).
  // Derivarlo de RECON_COLECCIONES evita que vuelva a pasar si se añade otra.
  const [reconStel, setReconStel] = useState(() =>
    Object.fromEntries(RECON_COLECCIONES.map(({ coleccion }) => [coleccion, RECON_VACIO()])));
  const [huerfanos, setHuerfanos] = useState(() =>
    Object.fromEntries(RECON_COLECCIONES.map(({ coleccion }) => [coleccion, HUERFANOS_VACIO()])));

  // Backfill nombreNorm
  const [backfilling, setBackfilling]   = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  // Backfill fecha de vencimiento de contratos
  const [vencRunning, setVencRunning] = useState(false);
  const [vencResult,  setVencResult]  = useState(null);

  const recargarCounts = useCallback(async () => {
    setLoadingCounts(true);
    try {
      const c = await contarColecciones();
      setCounts(c);
      const syncDoc = await getDoc(doc(db, 'config', 'stelorderSync'));
      setLastSync(syncDoc.exists() ? syncDoc.data().lastSyncAt : null);
    } finally {
      setLoadingCounts(false);
    }
  }, []);

  useEffect(() => { recargarCounts(); }, [recargarCounts]);

  // ── Import inicial ──────────────────────────────────────────────────────────

  // Pagina una sola tabla contra importarTodo.js hasta done:true, reportando
  // progreso a `setState`. Compartido por el import completo (las 10 tablas
  // seguidas) y por el botón de import individual por colección.
  const importarUnaTabla = async ({ tabla, label }, setState) => {
    setState(prev => ({ ...prev, [tabla]: { total: 0, done: false, label } }));
    let start = 0;
    let totalImportados = 0;
    let done = false;

    while (!done) {
      try {
        const res = await callFn('importarTodo', { tabla, start });
        if (!res.success) throw new Error(res.error || 'Error desconocido');
        totalImportados += res.importados || 0;
        done = res.done;
        start = res.nextStart ?? start + 500;
        setState(prev => ({ ...prev, [tabla]: { total: totalImportados, done, label } }));
      } catch (e) {
        setState(prev => ({ ...prev, [tabla]: { total: totalImportados, done: true, error: e.message, label } }));
        done = true;
      }
    }
  };

  const iniciarImport = async () => {
    setImportando(true);
    setImportState({});
    for (const item of TABLAS) {
      await importarUnaTabla(item, setImportState);
    }
    setImportando(false);
    recargarCounts();
  };

  // Import de una única colección (p.ej. solo "Productos"), sin tener que
  // repetir las otras 9 tablas — usa su propio estado (importUna), separado
  // del import completo, para no mezclar ambas barras de progreso.
  const importarTabla = async (item) => {
    setImportandoUna(item.tabla);
    await importarUnaTabla(item, setImportUna);
    setImportandoUna(null);
    recargarCounts();
  };

  // Vaciar + reimportar de cero — solo para catálogos pequeños (Productos/
  // Servicios, ver CATALOGO_STELORDER): más simple y barato que buscar
  // huérfanos cuando StelOrder se acaba de purgar de golpe.
  const vaciarYReimportar = async (item) => {
    const n = counts[item.coleccion];
    if (!window.confirm(
      `Esto borrará los ${n != null ? n.toLocaleString('es-ES') : ''} documentos de "${item.label}" sincronizados desde StelOrder (los creados a mano en Almacén no se tocan) y los volverá a traer desde cero.\n\n¿Continuar?`
    )) return;
    if (!confirmarBorradoDefinitivo()) return;

    setVaciando(item.tabla);
    try {
      // Vía Netlify Function (servicio, bypass de firestore.rules) — el
      // borrado directo desde el cliente está bloqueado a propósito
      // ("allow delete: if false" en productos/servicios, ver
      // firestore.rules): encontrado al probar esto mismo en real
      // ("Missing or insufficient permissions", Rafa, 2026-10-15).
      const res = await callFn('huerfanosStelOrder', { coleccion: item.coleccion, modo: 'vaciar', confirmar: 'BORRAR' });
      if (!res.success) throw new Error(res.error || 'Error desconocido');
      await importarUnaTabla(item, setImportUna);
    } catch (e) {
      setImportUna(prev => ({ ...prev, [item.tabla]: { total: 0, done: true, error: e.message, label: item.label } }));
    } finally {
      setVaciando(null);
      recargarCounts();
    }
  };

  const regenerarCatalogoOffline = async () => {
    setRegenerandoCatalogo(true);
    setCatalogoResult(null);
    try {
      const res = await callFn('generarCatalogoProductos', {});
      if (!res.success) throw new Error(res.error || 'Error desconocido');
      setCatalogoResult({ ok: true, count: res.count });
    } catch (e) {
      setCatalogoResult({ ok: false, error: e.message });
    } finally {
      setRegenerandoCatalogo(false);
    }
  };

  // ── Sync manual ────────────────────────────────────────────────────────────

  const syncManual = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      // syncDeltaStelOrder.js exige un token de servidor a servidor que el
      // navegador nunca puede tener — forzarSyncDeltaStelOrder.js lo invoca
      // en proceso, con la sesión de admin como única puerta (mismo patrón
      // que forzarSyncStelOrder.js para el sync del técnico).
      const res = await callFn('forzarSyncDeltaStelOrder', {});
      setSyncResult(res);
      recargarCounts();
    } catch (e) {
      setSyncResult({ success: false, error: e.message });
    } finally {
      setSyncing(false);
    }
  };

  // ── Facturas: histórico completo (a mano, pocas veces) ────────────────────
  // El cron automático de syncFacturas.js va en modo delta (solo lo más
  // recientemente modificado) para no repetir llamadas a StelOrder sin
  // necesidad — este botón es el único sitio que pide TODO el histórico
  // paginando de verdad, pensado para usarse una vez (o si alguna vez hace
  // falta recomponer algo), no en el día a día.
  // Una página (500) por llamada — pedir todo el histórico dentro de una
  // sola invocación llegó a dar 502 Bad Gateway con datos reales (más
  // páginas de las esperadas × latencia real de StelOrder > 26s). Se repite
  // la llamada con offset creciente hasta que StelOrder diga done:true,
  // mismo patrón que iniciarImport() un poco más abajo para las demás tablas.
  const importarFacturasCompleto = async () => {
    setFacturasImporting(true);
    setFacturasResult(null);
    let offset = 0;
    const acumulado = { facturas: 0, ok: 0, fail: 0, linked: 0, linkedFail: 0, diagnostico: null };
    try {
      for (let pagina = 0; pagina < 20; pagina++) { // tope de 20×500 = 10.000, de sobra
        const res = await callFn('forzarSyncFacturas', { full: true, offset });
        if (!res.success) { setFacturasResult(res); return; }
        acumulado.facturas += res.facturas || 0;
        acumulado.ok        += res.ok || 0;
        acumulado.fail      += res.fail || 0;
        acumulado.linked    += res.linked || 0;
        acumulado.linkedFail += res.linkedFail || 0;
        if (res.diagnostico) acumulado.diagnostico = res.diagnostico; // solo llega en la primera página
        setFacturasResult({ success: true, ...acumulado, enCurso: !res.done });
        if (res.done) break;
        offset = res.nextOffset;
      }
      recargarCounts();
    } catch (e) {
      setFacturasResult({ success: false, error: e.message });
    } finally {
      setFacturasImporting(false);
    }
  };

  // ── Reconciliar preliquidación Ariston ────────────────────────────────────
  const reconciliarPreliq = async () => {
    setReconPreliq({ running: true, revisadas: 0, corregidas: 0, erroresStel: 0, quedan: null, total: null, error: null, done: false });
    let revisadasAcc = 0, corregidasAcc = 0, erroresAcc = 0, vueltas = 0, fallosSeguidos = 0, tandasBloqueadas = 0;
    const wait = (ms) => new Promise(r => setTimeout(r, ms));
    try {
      while (vueltas < 400) {
        vueltas++;
        let res;
        try {
          // Tandas cortas: cada llamada debe terminar bastante por debajo del
          // límite de request de Netlify (~26 s) — 8 órdenes × ~1,2 s ≈ 12 s.
          res = await callFn('reconciliarPreliqAriston', { limite: 8 });
          if (!res || res.success !== true) throw new Error(res?.error || 'Respuesta no válida');
          fallosSeguidos = 0;
        } catch (e) {
          fallosSeguidos++;
          // Un 504 o corte de StelOrder es transitorio: espera y reintenta esa
          // misma tanda. Tras 3 fallos seguidos se para (el trabajo hecho queda
          // guardado — se puede volver a pulsar más tarde para continuar).
          if (fallosSeguidos >= 3) throw new Error(`${e.message} — parado tras 3 intentos. El avance está guardado; vuelve a pulsar en un rato para continuar.`);
          setReconPreliq(s => ({ ...s, error: `Reintentando… (${e.message})` }));
          await wait(4000);
          continue;
        }
        revisadasAcc += res.revisadas || 0;
        corregidasAcc += res.corregidas || 0;
        erroresAcc += res.errores || 0;
        setReconPreliq({
          running: true, revisadas: revisadasAcc, corregidas: corregidasAcc, erroresStel: erroresAcc,
          quedan: res.quedan, total: res.pendientesAntes ?? res.totalEnFirebase, error: null, done: false,
        });
        recargarCounts();
        if ((res.quedan || 0) === 0 || (res.revisadas || 0) === 0) break;
        // Si StelOrder devuelve error en TODA la tanda es que su API está
        // bloqueada ahora mismo. Se intenta un par de veces con pausa y si
        // sigue igual se para — no tiene sentido seguir dando vueltas.
        if ((res.errores || 0) >= (res.revisadas || 0) && (res.revisadas || 0) > 0) {
          tandasBloqueadas++;
          if (tandasBloqueadas >= 3) {
            throw new Error('StelOrder tiene la API bloqueada ahora mismo — no se puede reconciliar. Vuelve a intentarlo más tarde. Mientras, el listado ya oculta las órdenes de más de 12 meses (que son casi todo el desajuste).');
          }
          setReconPreliq(s => ({ ...s, error: `StelOrder rechazando (${tandasBloqueadas}/3) — pausa de 60 s…` }));
          await wait(60000);
        } else {
          tandasBloqueadas = 0;
        }
      }
      setReconPreliq(s => ({ ...s, running: false, done: true, error: null }));
    } catch (e) {
      setReconPreliq(s => ({ ...s, running: false, error: e.message }));
    }
  };

  // ── Reconciliar con StelOrder (órdenes/incidencias) ────────────────────────
  // A diferencia de "Limpiar todo Firebase" + "Import inicial" (borra y vuelve
  // a traer todo, con la app sin esos datos mientras dura), esto recorre
  // StelOrder página a página y solo toca lo que falte o esté desactualizado,
  // sin pisar nunca un cambio local aún no sincronizado (stelSyncEstado=
  // 'pendiente'). Al terminar, permite detectar y — solo si se confirma —
  // borrar lo que quedó huérfano (existe en Firebase pero ya no en StelOrder).

  const reconciliarStelOrder = async (coleccion) => {
    setReconStel(prev => ({ ...prev, [coleccion]: RECON_VACIO() }));
    setHuerfanos(prev => ({ ...prev, [coleccion]: HUERFANOS_VACIO() }));
    let start = 0, done = false;
    const acc = { creados: 0, actualizados: 0, coinciden: 0, omitidosPendientes: 0, errores: 0 };
    try {
      while (!done) {
        const res = await callFn('reconciliarConStelOrder', { coleccion, start });
        if (!res.success) throw new Error(res.error || 'Error desconocido');
        acc.creados += res.creados || 0;
        acc.actualizados += res.actualizados || 0;
        acc.coinciden += res.coinciden || 0;
        acc.omitidosPendientes += res.omitidosPendientes || 0;
        acc.errores += res.errores || 0;
        done = res.done;
        start = res.nextStart ?? start + 500;
        setReconStel(prev => ({ ...prev, [coleccion]: { running: true, ...acc, done: false, error: null } }));
      }
      setReconStel(prev => ({ ...prev, [coleccion]: { running: false, ...acc, done: true, error: null } }));
      recargarCounts();
    } catch (e) {
      setReconStel(prev => ({ ...prev, [coleccion]: { ...prev[coleccion], running: false, error: e.message } }));
    }
  };

  const compararHuerfanos = async (coleccion) => {
    setHuerfanos(prev => ({ ...prev, [coleccion]: { ...prev[coleccion], loading: true, error: null } }));
    try {
      const res = await callFn('huerfanosStelOrder', { coleccion, modo: 'comparar' });
      if (!res.success) throw new Error(res.error || 'Error desconocido');
      setHuerfanos(prev => ({ ...prev, [coleccion]: { loading: false, count: res.count, ids: res.huerfanos, totalFirebase: res.totalFirebase, totalStelOrder: res.totalStelOrder, error: null, borrando: false, borrado: null } }));
    } catch (e) {
      setHuerfanos(prev => ({ ...prev, [coleccion]: { ...prev[coleccion], loading: false, error: e.message } }));
    }
  };

  const borrarHuerfanos = async (coleccion) => {
    const label = RECON_COLECCIONES.find(c => c.coleccion === coleccion)?.label.toLowerCase() || coleccion;
    const n = huerfanos[coleccion].count || 0;
    if (!window.confirm(`Esto borrará definitivamente ${n} ${label} de Firebase que ya no existen en StelOrder.\n\n¿Continuar?`)) return;
    if (!confirmarBorradoDefinitivo()) return;

    setHuerfanos(prev => ({ ...prev, [coleccion]: { ...prev[coleccion], borrando: true, error: null } }));
    try {
      const res = await callFn('huerfanosStelOrder', { coleccion, modo: 'borrar', confirmar: 'BORRAR' });
      if (!res.success) throw new Error(res.error || 'Error desconocido');
      setHuerfanos(prev => ({ ...prev, [coleccion]: { ...HUERFANOS_VACIO(), borrado: res.borrados } }));
      recargarCounts();
    } catch (e) {
      setHuerfanos(prev => ({ ...prev, [coleccion]: { ...prev[coleccion], borrando: false, error: e.message } }));
    }
  };

  // ── Import Ariston (estados específicos no cubiertos por paginación general) ─

  const importarAriston = async () => {
    setImportandoAriston(true);
    setAristonResult(null);
    let totalOk = 0;
    try {
      // preliq_ariston: document-state-id=1041994
      let start = 0, done = false;
      while (!done) {
        const res = await callFn('importarTodo', { tabla: 'workOrders', start, filtro: 'document-state-id=1041994' });
        if (!res.success) throw new Error(res.error || 'Error');
        totalOk += res.importados || 0;
        done = res.done;
        start = res.nextStart ?? start + 500;
      }
      setAristonResult({ success: true, importados: totalOk });
      recargarCounts();
    } catch (e) {
      setAristonResult({ success: false, error: e.message });
    } finally {
      setImportandoAriston(false);
    }
  };

  // ── Backfill fecha de vencimiento de contratos ─────────────────────────────
  const backfillVencimiento = async (apply) => {
    setVencRunning(true);
    if (!apply) setVencResult(null);
    try {
      const res = await fetch(`${API_BASE}/backfillFechaVencimientoContrato`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ apply: !!apply }),
      });
      const data = await res.json();
      setVencResult(data.success ? data : { success: false, error: data.error });
    } catch (e) {
      setVencResult({ success: false, error: e.message });
    } finally {
      setVencRunning(false);
    }
  };

  // ── Backfill nombreNorm ─────────────────────────────────────────────────────

  const backfillNombres = async () => {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const res = await fetch('/.netlify/functions/backfillNombreNorm');
      const data = await res.json();
      setBackfillResult(data.success ? { success: true, patched: data.patched, skipped: data.skipped } : { success: false, error: data.error });
    } catch (e) {
      setBackfillResult({ success: false, error: e.message });
    } finally {
      setBackfilling(false);
    }
  };

  // ── Limpiar todo Firebase ───────────────────────────────────────────────────

  const limpiarTodo = async () => {
    if (!window.confirm(
      '⚠️ Esto borrará TODOS los datos de Firebase (órdenes, clientes, activos, incidencias, facturas, productos...).\n\n¿Continuar?'
    )) return;
    if (!confirmarBorradoDefinitivo()) return;

    setLimpiando(true);
    setLimpiezaLog([]);

    for (const { coleccion, label } of TABLAS) {
      setLimpiezaLog(prev => [...prev, `Limpiando ${label}...`]);
      try {
        const deleted = await limpiarColeccion(coleccion);
        setLimpiezaLog(prev => [...prev.slice(0, -1), `✓ ${label}: ${deleted} eliminados`]);
      } catch (e) {
        setLimpiezaLog(prev => [...prev.slice(0, -1), `✗ ${label}: ${e.message}`]);
      }
    }

    setLimpiando(false);
    recargarCounts();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const importDone = Object.values(importState).every(s => s.done);
  const importStarted = Object.keys(importState).length > 0;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Sincronización StelOrder</h1>
        <p className="text-sm text-gray-500 mt-1">
          Importación inicial + sync automático cada 30 min
          {lastSync && (
            <span className="ml-2 text-gray-400">
              · Último sync: {new Date(lastSync).toLocaleString('es-ES')}
            </span>
          )}
        </p>
      </div>

      {/* Estado de Firebase */}
      <section className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-800">Estado de Firebase</h2>
          <button
            onClick={recargarCounts}
            disabled={loadingCounts}
            className="text-xs text-blue-600 hover:underline disabled:opacity-50"
          >
            {loadingCounts ? 'Cargando...' : 'Actualizar'}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {TABLAS.map((item) => {
            const { tabla, coleccion, label } = item;
            const s = importUna[tabla];
            const enCurso = importandoUna === tabla;
            return (
              <div key={coleccion} className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">{label}</div>
                <div className="flex items-end justify-between gap-2">
                  <div className="text-xl font-bold text-gray-800">
                    {counts[coleccion] == null ? '—' : counts[coleccion].toLocaleString('es-ES')}
                  </div>
                  <button
                    onClick={() => importarTabla(item)}
                    disabled={!!importandoUna || importando || !!vaciando}
                    className="text-[11px] text-blue-600 hover:underline disabled:opacity-40 disabled:no-underline whitespace-nowrap"
                    title={`Reimportar solo ${label} desde StelOrder`}
                  >
                    {enCurso ? 'Importando…' : 'Reimportar'}
                  </button>
                </div>
                {s && (
                  <div className={`text-[11px] mt-1 ${s.error ? 'text-red-600' : s.done ? 'text-green-700' : 'text-blue-700'}`}>
                    {s.error ? s.error : `${s.total.toLocaleString('es-ES')} registros${s.done ? ' ✓' : '...'}`}
                  </div>
                )}
                {/* Solo para catálogos pequeños (ver CATALOGO_STELORDER) —
                    comparar huérfanos contra miles de órdenes/clientes reales
                    no es lo mismo que contra unos cientos de productos, y
                    para esas colecciones grandes sigue siendo mejor la
                    reconciliación de huérfanos (abajo), no un vaciado total. */}
                {CATALOGO_STELORDER.has(coleccion) && (
                  <button
                    onClick={() => vaciarYReimportar(item)}
                    disabled={!!importandoUna || importando || !!vaciando}
                    className="text-[11px] text-red-500 hover:underline disabled:opacity-40 disabled:no-underline whitespace-nowrap mt-1"
                    title={`Borrar todo "${label}" (lo sincronizado desde StelOrder) y reimportar desde cero`}
                  >
                    {vaciando === tabla ? 'Vaciando y reimportando…' : 'Vaciar y reimportar'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-400 mt-3">
          ℹ️ Facturas y abonos no están en esta lista a propósito — tienen su propia tarjeta justo debajo.
        </p>
      </section>

      {/* Catálogo offline de productos (buscador de material del técnico) */}
      <section className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-800 mb-1">Catálogo offline de productos (técnicos)</h2>
        <p className="text-sm text-gray-500 mb-4">
          El buscador de material en "Finalizar Visita" no mira Firestore en cada tecleo — usa un catálogo
          compacto que un cron regenera de madrugada (generarCatalogoProductos.js). Si acabas de limpiar o
          reimportar Productos, fuerza la regeneración aquí para no esperar a esta noche.
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={regenerarCatalogoOffline}
            disabled={regenerandoCatalogo}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-900 disabled:opacity-50 text-white font-semibold rounded-lg text-sm transition-colors"
          >
            {regenerandoCatalogo ? 'Regenerando…' : 'Regenerar catálogo ahora'}
          </button>
          {catalogoResult && (
            <span className={`text-sm ${catalogoResult.ok ? 'text-green-700' : 'text-red-600'}`}>
              {catalogoResult.ok ? `✓ ${catalogoResult.count.toLocaleString('es-ES')} productos` : catalogoResult.error}
            </span>
          )}
        </div>
      </section>

      {/* Facturas: histórico completo + sync delta automático */}
      <section className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-800 mb-1">Facturas — histórico completo</h2>
        <p className="text-sm text-gray-500 mb-4">
          No están en el import genérico de arriba porque necesitan un esquema propio (curado, no en bruto) —
          syncFacturas.js ya las mantiene al día automáticamente cada 30 min, pero solo mirando lo modificado
          recientemente (para no repetir llamadas a StelOrder sin necesidad). Usa este botón una vez para traer
          también el histórico completo — pagina de verdad, no se queda en las 500 más recientes.
        </p>
        <button
          onClick={importarFacturasCompleto}
          disabled={facturasImporting}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {facturasImporting ? 'Importando histórico completo…' : 'Importar histórico completo'}
        </button>
        {facturasResult && (
          <div className={`mt-3 text-sm rounded-lg p-3 ${facturasResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
            {facturasResult.success ? (
              <div>
                {facturasResult.enCurso ? '⏳ ' : '✓ '}
                {facturasResult.facturas} facturas revisadas · {facturasResult.ok} guardadas correctamente
                {facturasResult.fail > 0 && `, ${facturasResult.fail} con error`}
                {facturasResult.linked > 0 && ` · ${facturasResult.linked} órdenes enlazadas`}
                {facturasResult.enCurso && ' — siguiendo con la próxima página…'}
              </div>
            ) : (
              <div>Error: {facturasResult.error}</div>
            )}
          </div>
        )}
        {/* Diagnóstico del cruce de cobros — antes solo iba a los logs de
            Netlify (Functions → syncFacturas → Logs), un sitio al que nadie
            llega sin indicaciones paso a paso. Se enseña aquí directamente:
            camposFacturaRelacionadosConPago vacío o "totalRecibos: 0" suele
            ser la pista de por qué las facturas no se marcan cobradas. */}
        {facturasResult?.diagnostico && (
          <details className="mt-3 text-xs text-gray-500">
            <summary className="cursor-pointer font-semibold text-gray-600">
              Diagnóstico del cruce de cobros (por qué algo puede salir "pendiente" cuando no debería)
            </summary>
            <pre className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(facturasResult.diagnostico, null, 2)}
            </pre>
          </details>
        )}
      </section>

      {/* Import inicial */}
      <section className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-800 mb-1">Import inicial</h2>
        <p className="text-sm text-gray-500 mb-4">
          Pagina todas las tablas de StelOrder y las carga en Firebase.
          Puede tardar varios minutos la primera vez.
        </p>

        {!importando && !importStarted && (
          <button
            onClick={iniciarImport}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Iniciar import completo
          </button>
        )}

        {importStarted && (
          <div className="space-y-2">
            {TABLAS.map(({ tabla, label }) => {
              const s = importState[tabla];
              if (!s) return (
                <div key={tabla} className="flex items-center gap-3 text-sm text-gray-400">
                  <div className="w-4 h-4 rounded-full bg-gray-200" />
                  <span>{label}</span>
                </div>
              );
              return (
                <div key={tabla} className="flex items-center gap-3 text-sm">
                  {s.error ? (
                    <div className="w-4 h-4 rounded-full bg-red-400 flex-shrink-0" />
                  ) : s.done ? (
                    <div className="w-4 h-4 rounded-full bg-green-500 flex-shrink-0" />
                  ) : (
                    <div className="w-4 h-4 rounded-full bg-blue-400 animate-pulse flex-shrink-0" />
                  )}
                  <span className={s.error ? 'text-red-600' : s.done ? 'text-gray-700' : 'text-blue-700 font-medium'}>
                    {label}
                  </span>
                  <span className="text-gray-400 ml-auto">
                    {s.error
                      ? s.error
                      : `${s.total.toLocaleString('es-ES')} registros${s.done ? '' : '...'}`
                    }
                  </span>
                </div>
              );
            })}

            {!importando && importDone && (
              <div className="mt-3 flex gap-3">
                <div className="text-sm text-green-700 font-medium">Import completado</div>
                <button
                  onClick={() => setImportState({})}
                  className="text-xs text-gray-400 hover:underline"
                >
                  Reiniciar
                </button>
              </div>
            )}
            {importando && (
              <div className="text-xs text-gray-400 mt-2">No cierres esta ventana...</div>
            )}
          </div>
        )}
      </section>

      {/* Sync manual */}
      <section className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-800 mb-1">Sync manual</h2>
        <p className="text-sm text-gray-500 mb-4">
          Fuerza el sync ahora sin esperar al cron (últimos 500 registros modificados por tabla + estados Ariston).
        </p>
        <button
          onClick={syncManual}
          disabled={syncing}
          className="bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
        >
          {syncing ? 'Sincronizando...' : 'Sync ahora'}
        </button>

        {syncResult && (
          <div className={`mt-3 text-sm rounded-lg p-3 ${syncResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
            {syncResult.success ? (
              <>
                <div className="font-medium mb-1">Sync completado — {new Date(syncResult.syncAt).toLocaleString('es-ES')}</div>
                <div className="text-xs space-y-0.5">
                  {Object.entries(syncResult.resumen || {}).map(([tabla, r]) => (
                    <div key={tabla}>
                      {tabla}: {r.error ? `⚠ ${r.error}` : `${r.count} registros`}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div>Error: {syncResult.error}</div>
            )}
          </div>
        )}
      </section>

      {/* Import Ariston */}
      <section className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-800 mb-1">Import órdenes Ariston</h2>
        <p className="text-sm text-gray-500 mb-4">
          Importa específicamente las órdenes en estado preliq_ariston (1041994) que la paginación general no captura.
        </p>
        <button
          onClick={importarAriston}
          disabled={importandoAriston}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
        >
          {importandoAriston ? 'Importando...' : 'Importar Ariston preliq'}
        </button>

        {aristonResult && (
          <div className={`mt-3 text-sm rounded-lg p-3 ${aristonResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
            {aristonResult.success
              ? `✓ ${aristonResult.importados} órdenes Ariston importadas`
              : `Error: ${aristonResult.error}`
            }
          </div>
        )}
      </section>

      {/* Reconciliar preliquidación Ariston */}
      <section className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-800 mb-1">Reconciliar preliquidación Ariston</h2>
        <p className="text-sm text-gray-500 mb-4">
          Recorre las órdenes que Firebase todavía marca como preliquidación Ariston y comprueba en StelOrder
          su estado real: las que ya se liquidaron o cambiaron de estado se corrigen en Firebase, así el contador
          del listado de liquidaciones deja de estar inflado. Va en tandas pequeñas respetando el límite de la API
          (tarda un rato si hay muchas). Se puede parar y retomar: cada vuelta continúa donde lo dejó.
        </p>
        <button
          onClick={reconciliarPreliq}
          disabled={reconPreliq.running}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
        >
          {reconPreliq.running ? 'Reconciliando…' : 'Reconciliar ahora'}
        </button>
        {(reconPreliq.running || reconPreliq.done || reconPreliq.error) && (
          <div className={`mt-3 text-sm rounded-lg p-3 ${reconPreliq.error ? 'bg-red-50 text-red-700' : reconPreliq.done ? 'bg-green-50 text-green-800' : 'bg-blue-50 text-blue-800'}`}>
            {reconPreliq.error
              ? `Error: ${reconPreliq.error}`
              : (
                <>
                  <div>{reconPreliq.revisadas} revisadas · <strong>{reconPreliq.corregidas}</strong> corregidas
                    {reconPreliq.quedan != null && ` · ${reconPreliq.quedan} por revisar`}
                    {reconPreliq.erroresStel > 0 && ` · ${reconPreliq.erroresStel} sin respuesta de StelOrder`}</div>
                  {reconPreliq.done && <div className="font-medium mt-1">✓ Reconciliación terminada{reconPreliq.quedan > 0 ? ` — quedan ${reconPreliq.quedan}, vuelve a pulsar para seguir` : ''}</div>}
                </>
              )}
          </div>
        )}
      </section>

      {/* Reconciliar con StelOrder: órdenes e incidencias */}
      <section className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-800 mb-1">Reconciliar con StelOrder</h2>
        <p className="text-sm text-gray-500 mb-4">
          Compara Firebase contra StelOrder registro a registro, solo para <strong>órdenes</strong> e <strong>incidencias</strong>
          (nunca contratos de mantenimiento ni el resto de tablas): crea lo que falte, actualiza lo desactualizado y
          nunca pisa un cambio local que aún no se subió a StelOrder. No borra ni vuelve a traer todo — la app sigue
          funcionando con normalidad mientras corre. Al terminar, comprueba si hay <em>huérfanos</em> (lo que hay en
          Firebase y ya se eliminó en StelOrder) y bórralos aparte, con confirmación explícita.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {RECON_COLECCIONES.map(({ coleccion, label }) => {
            const r = reconStel[coleccion];
            const h = huerfanos[coleccion];
            return (
              <div key={coleccion} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-800">{label}</h3>
                  <button
                    onClick={() => reconciliarStelOrder(coleccion)}
                    disabled={r.running}
                    className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {r.running ? 'Comparando…' : 'Comparar y sincronizar'}
                  </button>
                </div>

                {(r.running || r.done || r.error) && (
                  <div className={`text-xs rounded-lg p-2 mb-2 ${r.error ? 'bg-red-50 text-red-700' : r.done ? 'bg-green-50 text-green-800' : 'bg-blue-50 text-blue-800'}`}>
                    {r.error ? `Error: ${r.error}` : (
                      <>
                        <div>{r.creados} creados · {r.actualizados} actualizados · {r.coinciden} sin cambios{r.omitidosPendientes > 0 && ` · ${r.omitidosPendientes} con cambio local pendiente (no tocados)`}{r.errores > 0 && ` · ${r.errores} errores`}</div>
                        {r.done && <div className="font-medium mt-1">✓ Sincronización terminada</div>}
                      </>
                    )}
                  </div>
                )}

                {r.done && !r.error && (
                  <div className="border-t border-gray-100 pt-2">
                    <button
                      onClick={() => compararHuerfanos(coleccion)}
                      disabled={h.loading}
                      className="text-xs text-indigo-600 hover:underline disabled:opacity-50"
                    >
                      {h.loading ? 'Comprobando huérfanos…' : 'Comprobar huérfanos'}
                    </button>

                    {h.error && <div className="text-xs text-red-600 mt-1">{h.error}</div>}

                    {h.count != null && (
                      <div className="mt-2 text-xs">
                        {h.count === 0 ? (
                          <div className="text-green-700">✓ No hay huérfanos ({h.totalFirebase} en Firebase, todos vistos en StelOrder)</div>
                        ) : (
                          <>
                            <div className="text-amber-700 mb-1">
                              {h.count} {coleccion} en Firebase ya no existen en StelOrder (de {h.totalFirebase} totales)
                            </div>
                            <div className="max-h-20 overflow-y-auto bg-gray-50 rounded p-1.5 font-mono text-[11px] text-gray-600 mb-1.5">
                              {h.ids.slice(0, 50).join(', ')}{h.count > 50 ? '…' : ''}
                            </div>
                            <button
                              onClick={() => borrarHuerfanos(coleccion)}
                              disabled={h.borrando}
                              className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-red-700 disabled:opacity-50"
                            >
                              {h.borrando ? 'Borrando…' : `Borrar ${h.count} huérfanos`}
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {h.borrado != null && (
                      <div className="text-xs text-green-700 mt-2">✓ {h.borrado} huérfanos eliminados</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Reindexar nombres clientes */}
      <section className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-800 mb-1">Reindexar nombres de clientes CRM</h2>
        <p className="text-sm text-gray-500 mb-4">
          Genera el campo <code>nombreNorm</code> y normaliza el teléfono en clientes de Firebase que los tengan vacíos.
          Necesario para que la búsqueda del CRM propio funcione correctamente.
        </p>
        <button
          onClick={backfillNombres}
          disabled={backfilling}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {backfilling ? 'Reindexando...' : 'Reindexar nombres'}
        </button>
        {backfillResult && (
          <div className={`mt-3 text-sm rounded-lg p-3 ${backfillResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
            {backfillResult.success
              ? `✓ ${backfillResult.patched} clientes actualizados · ${backfillResult.skipped} ya estaban correctos`
              : `Error: ${backfillResult.error}`}
          </div>
        )}
      </section>

      {/* Corregir fecha de vencimiento de contratos */}
      <section className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-800 mb-1">Corregir vencimiento de contratos de mantenimiento</h2>
        <p className="text-sm text-gray-500 mb-4">
          Detecta contratos cuya <code>fechaVencimientoContrato</code> quedó mal (vacía, igual a la fecha del
          próximo mantenimiento, anterior a la fecha de inicio o con menos de 10 meses de duración) y la
          recalcula como <strong>fecha de inicio + 1 año × (nº de renovaciones + 1)</strong>.
          Primero pulsa <em>Revisar</em> para ver el listado; nada se toca hasta que pulses <em>Aplicar</em>.
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => backfillVencimiento(false)}
            disabled={vencRunning}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {vencRunning ? 'Procesando…' : 'Revisar'}
          </button>
          {vencResult?.success && vencResult.totalACorregir > 0 && vencResult.modo === 'simulacion' && (
            <button
              onClick={() => backfillVencimiento(true)}
              disabled={vencRunning}
              className="bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
            >
              Aplicar a {vencResult.totalACorregir} contrato{vencResult.totalACorregir !== 1 ? 's' : ''}
            </button>
          )}
        </div>
        {vencResult && (
          <div className={`mt-3 text-sm rounded-lg p-3 ${vencResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
            {!vencResult.success
              ? `Error: ${vencResult.error}`
              : vencResult.modo === 'aplicado'
                ? `✓ ${vencResult.corregidos} contratos corregidos de ${vencResult.totalACorregir} detectados`
                : vencResult.totalACorregir === 0
                  ? `✓ ${vencResult.revisados} contratos revisados — ninguno necesita corrección`
                  : (
                    <>
                      <p className="font-semibold mb-1">{vencResult.totalACorregir} contratos con la fecha mal (de {vencResult.revisados} revisados):</p>
                      <div className="max-h-64 overflow-y-auto border border-green-200 rounded bg-white">
                        <table className="w-full text-xs">
                          <thead className="bg-green-100 text-green-900">
                            <tr><th className="text-left px-2 py-1">Cliente</th><th className="text-left px-2 py-1">Inicio</th><th className="text-left px-2 py-1">Venc. actual</th><th className="text-left px-2 py-1">Próx. mant.</th><th className="text-left px-2 py-1">Venc. nuevo</th></tr>
                          </thead>
                          <tbody>
                            {vencResult.aCorregir.map((c) => (
                              <tr key={c.id} className="border-t border-green-100">
                                <td className="px-2 py-1">{c.cliente || c.id}</td>
                                <td className="px-2 py-1">{c.inicioContrato || '—'}</td>
                                <td className="px-2 py-1 text-red-600">{c.vencActual || '(vacío)'}</td>
                                <td className="px-2 py-1 text-gray-500">{c.proximoMant || '—'}</td>
                                <td className="px-2 py-1 font-semibold text-green-700">{c.vencNuevo}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {vencResult.totalACorregir > vencResult.aCorregir.length && (
                        <p className="mt-1 text-xs text-green-700">(se muestran los primeros {vencResult.aCorregir.length})</p>
                      )}
                    </>
                  )}
          </div>
        )}
      </section>

      {/* Zona peligrosa */}
      <section className="bg-red-50 border border-red-200 rounded-xl p-5">
        <h2 className="font-semibold text-red-800 mb-1">Zona peligrosa</h2>
        <p className="text-sm text-red-600 mb-4">
          Elimina todos los registros de Firebase en todas las colecciones.
          Úsalo antes de un import inicial limpio.
        </p>

        <button
          onClick={limpiarTodo}
          disabled={limpiando}
          className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
        >
          {limpiando ? 'Limpiando...' : 'Limpiar todo Firebase'}
        </button>

        {limpiezaLog.length > 0 && (
          <div className="mt-3 text-xs font-mono space-y-0.5 text-red-700">
            {limpiezaLog.map((line, i) => <div key={i}>{line}</div>)}
          </div>
        )}
      </section>
    </div>
  );
}
